'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2ApplySuggestionPayload } from './pr-v2-suggestion-block';
import { extractSuggestionParts, sanitizeMrgeBotComment } from './pr-v2-comment-utils';
import { PrV2SuggestionBlock } from './pr-v2-suggestion-block';
import { MixedMarkdownMermaidRenderer } from '@/components/markdown-renderer/mixed-markdown-mermaid-renderer';
import { removeReplyFooter } from '@/lib/pr-v2/issue-comment-reply';
import { isMrgeBot } from '@/utils/github/bot-detection';
type NormalizePrV2CommentBodyArgs = {
  body: string | null;
  userLogin: string | null;
  inReplyToId?: string | null;
};
type PrV2CommentContentProps = {
  owner: string;
  repo: string;
  pullNumber?: string;
  filePath?: string;
  body: string | null;
  userLogin: string | null;
  inReplyToId?: string | null;
  id: string;
  /** Whether the viewer is on the public read-only page of a public repository. */
  isPublicView?: boolean;
  className?: string;
  emptyText?: string;
  suggestionOriginalLines?: string[];
  onApplySuggestion?: (payload: PrV2ApplySuggestionPayload) => Promise<void> | void;
  onApplySuggestionAndResolve?: (payload: PrV2ApplySuggestionPayload) => Promise<void> | void;
  applyDisabledReason?: string;
  isApplyingSuggestion?: boolean;
};
export function normalizePrV2CommentBody({
  body,
  userLogin,
  inReplyToId
}: NormalizePrV2CommentBodyArgs): string {
  const raw = body?.trim() ?? '';
  if (!raw) {
    return '';
  }
  const withoutReplyFooter = inReplyToId ? removeReplyFooter(raw) : raw;
  const normalized = isMrgeBot(userLogin) ? sanitizeMrgeBotComment(withoutReplyFooter) : withoutReplyFooter;
  return normalized.trim();
}
export function PrV2CommentContent(t0) {
  const $ = _c(27);
  const {
    owner,
    repo,
    pullNumber,
    filePath,
    body,
    userLogin,
    inReplyToId,
    id,
    isPublicView: t1,
    className,
    emptyText: t2,
    suggestionOriginalLines,
    onApplySuggestion,
    onApplySuggestionAndResolve,
    applyDisabledReason,
    isApplyingSuggestion: t3
  } = t0;
  const isPublicView = t1 === undefined ? false : t1;
  const emptyText = t2 === undefined ? "(no content)" : t2;
  const isApplyingSuggestion = t3 === undefined ? false : t3;
  let normalizedBody;
  let t4;
  if ($[0] !== body || $[1] !== inReplyToId || $[2] !== userLogin) {
    normalizedBody = normalizePrV2CommentBody({
      body,
      userLogin,
      inReplyToId
    });
    t4 = extractSuggestionParts(normalizedBody);
    $[0] = body;
    $[1] = inReplyToId;
    $[2] = userLogin;
    $[3] = normalizedBody;
    $[4] = t4;
  } else {
    normalizedBody = $[3];
    t4 = $[4];
  }
  const {
    suggestion,
    regularContent
  } = t4;
  const shouldRenderSuggestion = suggestion !== null && Boolean(pullNumber);
  const markdownContent = shouldRenderSuggestion ? regularContent : normalizedBody;
  if (!markdownContent && !shouldRenderSuggestion) {
    let t5;
    if ($[5] !== className || $[6] !== emptyText) {
      t5 = <p className={className}>{emptyText}</p>;
      $[5] = className;
      $[6] = emptyText;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  let t5;
  if ($[8] !== className || $[9] !== id || $[10] !== isPublicView || $[11] !== markdownContent || $[12] !== owner || $[13] !== repo) {
    t5 = markdownContent ? <MixedMarkdownMermaidRenderer content={markdownContent} org={owner} repo={repo} id={id} className={className} isPublicView={isPublicView} githubRender="uploads" fallbackToRawCodeOnError={true} linkifyCommitReferences={true} /> : null;
    $[8] = className;
    $[9] = id;
    $[10] = isPublicView;
    $[11] = markdownContent;
    $[12] = owner;
    $[13] = repo;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== applyDisabledReason || $[16] !== filePath || $[17] !== isApplyingSuggestion || $[18] !== onApplySuggestion || $[19] !== onApplySuggestionAndResolve || $[20] !== shouldRenderSuggestion || $[21] !== suggestion || $[22] !== suggestionOriginalLines) {
    t6 = shouldRenderSuggestion ? <PrV2SuggestionBlock content={suggestion ?? ""} filePath={filePath} originalLines={suggestionOriginalLines} onApplySuggestion={onApplySuggestion} onApplySuggestionAndResolve={onApplySuggestionAndResolve} applyDisabledReason={applyDisabledReason} isApplyingSuggestion={isApplyingSuggestion} /> : null;
    $[15] = applyDisabledReason;
    $[16] = filePath;
    $[17] = isApplyingSuggestion;
    $[18] = onApplySuggestion;
    $[19] = onApplySuggestionAndResolve;
    $[20] = shouldRenderSuggestion;
    $[21] = suggestion;
    $[22] = suggestionOriginalLines;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}